<template>
  <div>
    <v-row class="ma-0 my-4 align-center">
      <v-btn icon @click="goBack"><v-icon>mdi-arrow-left</v-icon></v-btn>
      <h1 class="text-h5 font-weight-bold ml-2">
        Servicios de {{ clinicName || 'la clínica' }}
      </h1>
    </v-row>

    <v-row v-if="can('droopy-clinics:manage-services')" class="ma-0 mb-2 justify-end actions-cell">
      <v-btn class="square-btn" small outlined @click="openBulkImportDialog">
        <v-icon left small>mdi-file-upload</v-icon>Carga masiva
      </v-btn>
      <v-btn color="primary" class="square-btn" small @click="openCreateDialog">
        <v-icon left small>mdi-plus</v-icon>Agregar servicio
      </v-btn>
    </v-row>

    <v-card outlined>
      <v-data-table
        :headers="headers"
        :items="services"
        item-key="id"
        :loading="loadingServices"
        no-data-text="Esta clínica todavía no tiene servicios configurados."
      >
        <template v-slot:item.pawerServices="{ item }">
          <v-chip v-for="ps in item.pawerServices" :key="ps.id" x-small class="mr-1">{{ ps.title }}</v-chip>
          <span v-if="!item.pawerServices.length" class="grey--text">—</span>
        </template>
        <template v-slot:item.visible="{ item }">
          <v-icon :color="item.visible ? 'success' : 'grey'">
            {{ item.visible ? 'mdi-check-circle' : 'mdi-close-circle' }}
          </v-icon>
        </template>
        <template v-slot:item.actions="{ item }">
          <div
            v-if="can('droopy-clinics:manage-services')"
            class="d-flex align-center justify-end actions-cell"
          >
            <v-tooltip top>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  fab
                  dark
                  depressed
                  x-small
                  v-bind="attrs"
                  v-on="on"
                  :color="item.visible ? 'error' : 'success'"
                  :loading="togglingId === item.id"
                  :disabled="!!togglingId"
                  @click="toggleVisible(item)"
                >
                  <v-icon small dark>{{ item.visible ? 'mdi-eye-off' : 'mdi-eye' }}</v-icon>
                </v-btn>
              </template>
              <span>{{ item.visible ? 'Deshabilitar' : 'Habilitar' }}</span>
            </v-tooltip>
            <v-tooltip top>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  fab
                  dark
                  depressed
                  x-small
                  v-bind="attrs"
                  v-on="on"
                  color="primary"
                  @click="openEditLinksDialog(item)"
                >
                  <v-icon small dark>mdi-link-variant</v-icon>
                </v-btn>
              </template>
              <span>Editar servicios Pawer relacionados</span>
            </v-tooltip>
          </div>
        </template>
      </v-data-table>
    </v-card>

    <v-dialog v-model="showCreateDialog" min-width="600" max-width="900" @keydown.esc="onCloseCreateDialog">
      <clinic-service-modal
        v-if="showCreateDialog"
        :clinic-id="clinicId"
        :service="null"
        @update-list="onUpdateList"
        @close-dialog="onCloseCreateDialog"
      ></clinic-service-modal>
    </v-dialog>

    <v-dialog v-model="showEditLinksDialog" min-width="500" max-width="700" @keydown.esc="onCloseEditLinksDialog">
      <edit-clinic-service-links-modal
        v-if="showEditLinksDialog"
        :clinic-id="clinicId"
        :service="selectedService"
        @update-list="onUpdateList"
        @close-dialog="onCloseEditLinksDialog"
      ></edit-clinic-service-links-modal>
    </v-dialog>

    <v-dialog v-model="showBulkImportDialog" max-width="1100">
      <clinic-services-bulk-import-modal
        v-if="showBulkImportDialog"
        :clinic-id="clinicId"
        @imported="onImported"
        @close-dialog="onCloseBulkImportDialog"
      ></clinic-services-bulk-import-modal>
    </v-dialog>

    <v-snackbar v-model="showSnackbar" :timeout="5000" :color="snackbarColor">
      {{ snackbarMessage }}
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="showSnackbar = false">Cerrar</v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
import ClinicsService from '../services/clinics.service';
import ClinicServiceModal from '../components/ClinicServiceModal.vue';
import EditClinicServiceLinksModal from '../components/EditClinicServiceLinksModal.vue';
import ClinicServicesBulkImportModal from '../components/ClinicServicesBulkImportModal.vue';
import { utilsMixin } from '../mixins/utilsMixin';
import { can } from '../utils/permissions';

export default {
  name: 'DroopyClinicServices',

  components: { ClinicServiceModal, EditClinicServiceLinksModal, ClinicServicesBulkImportModal },

  mixins: [utilsMixin],

  props: {
    clinicId: { type: String, required: true }
  },

  data() {
    return {
      services: [],
      loadingServices: false,
      togglingId: null,
      showCreateDialog: false,
      showEditLinksDialog: false,
      showBulkImportDialog: false,
      selectedService: null,
      headers: [
        { text: 'Nombre', value: 'title' },
        { text: 'Servicios Pawer asociados', value: 'pawerServices', sortable: false },
        { text: 'Visible', value: 'visible', sortable: false },
        { text: 'Acciones', value: 'actions', sortable: false, align: 'center' }
      ],
      showSnackbar: false,
      snackbarMessage: '',
      snackbarColor: ''
    };
  },

  computed: {
    clinicName() {
      return this.$route.query.clinicName || '';
    }
  },

  mounted() {
    this.fetchServices();
  },

  methods: {
    can,
    async fetchServices() {
      this.loadingServices = true;
      const res = await ClinicsService.getClinicServicesPawerMapping(this.clinicId);
      if (res.status === 'ok') {
        this.services = res.data;
      } else {
        this.showErrorSnackbar('No se pudieron cargar los servicios de la clínica.');
      }
      this.loadingServices = false;
    },

    openCreateDialog() {
      this.showCreateDialog = true;
    },

    onCloseCreateDialog() {
      this.showCreateDialog = false;
      this.fetchServices();
    },

    onUpdateList() {
      this.fetchServices();
    },

    openBulkImportDialog() {
      this.showBulkImportDialog = true;
    },

    onCloseBulkImportDialog() {
      this.showBulkImportDialog = false;
    },

    onImported(count) {
      this.showSuccessSnackbar(`Se crearon ${count} servicios.`);
      this.fetchServices();
    },

    openEditLinksDialog(item) {
      this.selectedService = item;
      this.showEditLinksDialog = true;
    },

    onCloseEditLinksDialog() {
      this.showEditLinksDialog = false;
      this.selectedService = null;
      this.fetchServices();
    },

    async toggleVisible(item) {
      this.togglingId = item.id;
      const newVisible = !item.visible;
      const res = await ClinicsService.updateClinicServiceVisibility(this.clinicId, item.id, newVisible);

      if (res.status === 'ok') {
        item.visible = newVisible;
      } else {
        this.showErrorSnackbar('No se pudo actualizar la visibilidad del servicio.');
      }
      this.togglingId = null;
    },

    goBack() {
      this.$router.push('/clinics/droopy');
    }
  }
};
</script>

<style scoped>
.actions-cell {
  gap: 8px;
}

.square-btn {
  border-radius: 8px !important;
}
</style>
